import React, { useState } from 'react';
import c from 'classnames';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';
import { getDeviationType } from '../../DeviationViews/_helpers';
import { useSubscriptionTier } from '../../utils/hooks/useSubscriptionTier';
import LoadingIndicator from '../../LoadingIndicator';
import ArtStage from '../../ArtStage';
import MonetizationStrip from '../../MonetizationStrip';
import ActionBar from '../../ActionBar';
import Sidebar from '../../Sidebar';
import Details from '../../DeviationViews/Details';
import SubPaywall from '../../DeviationViews/Full/_partials/SubPaywall';
import PanelPopup from '../../DeviationPage/_partials/PanelPopup';
import { DeviationTypes } from '../../types/deviation';
import { getCommentableTypeForDeviationType, isExtendedDeviationFullyLoaded } from '../redux/helpers';
import Comments from '../_partials/Comments';
import TierUpsell from '../_partials/TierUpsell';
import UserGiftSaleBanner from '../_partials/UserGiftSaleBanner';
import LiteratureTextualContent from '../../DeviationViews/TextualContent/Literature';
import AdoptableInfo from '../../Sidebar/SidebarBlock/layouts/AdoptableInfo/AdoptableInfo';
import PcpInfo from '../../Sidebar/SidebarBlock/layouts/PcpInfo/PcpInfo';
import MonetizationBlock from '../../Sidebar/SidebarBlock/layouts/MonetizationBlock';
import withHydrationOnDemand from 'react-hydration-on-demand';
const CommentsWithHydrationOnDemand = withHydrationOnDemand({
    on: [
        'visible'
    ]
})(Comments);
import s from './MobileDeviationPage.scss';
const MobileDeviationPage = ({ deviation })=>{
    var _deviation_extended_relatedContent, _deviation_extended, _deviation_extended_relatedContent1, _deviation_extended1, _deviation_extended_relatedContent2, _deviation_extended2;
    const isMobilePerfTest = useFlag('mobile_perf');
    const isPromoHigherMobileFlagEnabled = useFlag('promo_higher_mobile');
    const pcp_rail = useFlag('pcp_rail');
    const redesigned_money_banners = useFlag('redesigned_money_banners');
    const isExtendedLoaded = isExtendedDeviationFullyLoaded(deviation);
    const deviationType = getDeviationType(deviation);
    const isJournal = deviationType === DeviationTypes.JOURNAL;
    const isLiterature = deviationType === DeviationTypes.LITERATURE;
    const isJournalOrLit = isJournal || isLiterature;
    const { typeId, deviationId } = deviation;
    const commentableTypeId = getCommentableTypeForDeviationType(typeId);
    const { tier, isSubscribedButBlocked, needsSubscription } = useSubscriptionTier(deviation);
    const isBlocked = needsSubscription || isSubscribedButBlocked;
    // we'll force a re-render of the page when a countdown in the offer banner of the monetization strip
    // reaches 0 (onAdoptableSaleEnd). This will update the actionbar to change from the 'make an offer'
    // button to the 'view offers' button to show the sale is over
    const [hasAdoptionEnded, setHasAdoptionEnded] = useState(false);
    // TODO: Replace these hacks with a better approach to having sidebar blocks in a different zone
    const monetizationBlock = (_deviation_extended = deviation.extended) === null || _deviation_extended === void 0 ? void 0 : (_deviation_extended_relatedContent = _deviation_extended.relatedContent) === null || _deviation_extended_relatedContent === void 0 ? void 0 : _deviation_extended_relatedContent.find((block)=>block.contentType.includes('monetization_') || block.contentType === 'monetization_promotion_adoptable');
    const showAdoptableBlock = !!((_deviation_extended1 = deviation.extended) === null || _deviation_extended1 === void 0 ? void 0 : (_deviation_extended_relatedContent1 = _deviation_extended1.relatedContent) === null || _deviation_extended_relatedContent1 === void 0 ? void 0 : _deviation_extended_relatedContent1.find((block)=>block.contentType === 'adoptable_info'));
    const showPcpInfoBlock = pcp_rail && !!((_deviation_extended2 = deviation.extended) === null || _deviation_extended2 === void 0 ? void 0 : (_deviation_extended_relatedContent2 = _deviation_extended2.relatedContent) === null || _deviation_extended_relatedContent2 === void 0 ? void 0 : _deviation_extended_relatedContent2.find((block)=>block.contentType === 'pcp_info'));
    return /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement("div", {
        className: s['root']
    }, /*#__PURE__*/ React.createElement(ArtStage, {
        deviation: deviation
    }), /*#__PURE__*/ React.createElement(MonetizationStrip, {
        className: s['strip-tier-promotion'],
        deviation: deviation,
        onAdoptableSaleEnd: ()=>setHasAdoptionEnded(true)
    }), /*#__PURE__*/ React.createElement(ActionBar, {
        // maintain a key so we can force a re-render
        key: `actionbar-${hasAdoptionEnded}`,
        deviation: deviation
    }), isLiterature && /*#__PURE__*/ React.createElement("div", {
        className: s['lit-content']
    }, /*#__PURE__*/ React.createElement(LiteratureTextualContent, {
        deviation: deviation
    })), /*#__PURE__*/ React.createElement(PanelPopup, {
        deviation: deviation,
        actionBarType: "primary",
        className: s['large-popover-uplifted']
    }), /*#__PURE__*/ React.createElement(Details, {
        contentClassName: s['details'],
        deviation: deviation
    }), /*#__PURE__*/ React.createElement(UserGiftSaleBanner, {
        user: deviation.author
    }), isJournalOrLit && isBlocked && /*#__PURE__*/ React.createElement(SubPaywall, {
        deviation: deviation,
        showImage: false
    }), showAdoptableBlock && /*#__PURE__*/ React.createElement(AdoptableInfo, {
        deviation: deviation
    }), showPcpInfoBlock && /*#__PURE__*/ React.createElement(PcpInfo, {
        deviation: deviation
    }), isExtendedLoaded ? /*#__PURE__*/ React.createElement(React.Fragment, null, isPromoHigherMobileFlagEnabled && monetizationBlock && /*#__PURE__*/ React.createElement(MonetizationBlock, {
        className: c(redesigned_money_banners && s['money-promo']),
        block: monetizationBlock,
        deviation: deviation
    }), !isBlocked && (isMobilePerfTest ? /*#__PURE__*/ React.createElement(CommentsWithHydrationOnDemand, {
        key: `${commentableTypeId}:${deviationId}`,
        deviation: deviation,
        className: s['comments']
    }) : /*#__PURE__*/ React.createElement(Comments, {
        key: `${commentableTypeId}:${deviationId}`,
        deviation: deviation,
        className: s['comments']
    })), !isBlocked && !!tier && /*#__PURE__*/ React.createElement(TierUpsell, {
        className: s['upsell'],
        deviation: deviation
    }), /*#__PURE__*/ React.createElement("div", {
        className: s['sidebar']
    }, /*#__PURE__*/ React.createElement(Sidebar, {
        deviation: deviation
    }))) : /*#__PURE__*/ React.createElement(LoadingIndicator, {
        className: s['lloader'],
        view: "llama"
    }), isBlocked && !!tier && /*#__PURE__*/ React.createElement(TierUpsell, {
        className: s['upsell'],
        deviation: deviation
    })));
};
MobileDeviationPage.displayName = 'MobileDeviationPage';
export default MobileDeviationPage;
